import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

interface SEOHeadProps {
  title: string;
  description: string;
  keywords: string;
  canonicalPath?: string;
  ogType?: string;
  ogImage?: string;
  articleData?: {
    publishedTime?: string;
    modifiedTime?: string;
    author?: string;
  };
  faqData?: Array<{ question: string; answer: string }>;
}

export default function SEOHead({
  title,
  description,
  keywords,
  canonicalPath,
  ogType = 'website',
  ogImage,
  articleData,
  faqData,
}: SEOHeadProps) {
  const location = useLocation();

  useEffect(() => {
    document.title = title;

    const setMeta = (name: string, content: string, isProperty = false) => {
      const attr = isProperty ? 'property' : 'name';
      let el = document.querySelector(`meta[${attr}="${name}"]`);
      if (!el) {
        el = document.createElement('meta');
        el.setAttribute(attr, name);
        document.head.appendChild(el);
      }
      el.setAttribute('content', content);
    };

    setMeta('description', description);
    setMeta('keywords', keywords);

    const baseUrl = `${window.location.origin}${__BASE_PATH__}`;
    const canonical = canonicalPath || location.pathname;
    let canonicalEl = document.querySelector('link[rel="canonical"]');
    if (!canonicalEl) {
      canonicalEl = document.createElement('link');
      canonicalEl.setAttribute('rel', 'canonical');
      document.head.appendChild(canonicalEl);
    }
    canonicalEl.setAttribute('href', `${baseUrl}${canonical}`);

    setMeta('og:title', title, true);
    setMeta('og:description', description, true);
    setMeta('og:type', ogType, true);
    setMeta('og:url', `${baseUrl}${canonical}`, true);
    setMeta('og:site_name', '韩漫网', true);
    if (ogImage) {
      setMeta('og:image', ogImage, true);
    }

    setMeta('twitter:card', 'summary_large_image');
    setMeta('twitter:title', title);
    setMeta('twitter:description', description);

    if (articleData) {
      if (articleData.publishedTime) {
        setMeta('article:published_time', articleData.publishedTime);
      }
      if (articleData.modifiedTime) {
        setMeta('article:modified_time', articleData.modifiedTime);
      }
      if (articleData.author) {
        setMeta('article:author', articleData.author);
      }
    }
  }, [title, description, keywords, canonicalPath, ogType, ogImage, articleData, faqData, location.pathname]);

  const buildSchema = () => {
    const baseUrl = `${window.location.origin}${__BASE_PATH__}`;
    const canonical = canonicalPath || location.pathname;

    const schemas: Record<string, unknown>[] = [];

    schemas.push({
      '@context': 'https://schema.org',
      '@type': 'Organization',
      name: '韩漫网',
      url: baseUrl,
      description: '韩漫网 - 免费在线漫画阅读平台，提供高清韩国漫画大全，资源齐全界面简洁。',
    });

    schemas.push({
      '@context': 'https://schema.org',
      '@type': 'WebSite',
      name: '韩漫网',
      url: baseUrl,
      potentialAction: {
        '@type': 'SearchAction',
        target: `${baseUrl}/search?q={search_term_string}`,
        'query-input': 'required name=search_term_string',
      },
    });

    schemas.push({
      '@context': 'https://schema.org',
      '@type': 'WebPage',
      name: title,
      description: description,
      url: `${baseUrl}${canonical}`,
    });

    if (ogType === 'article' && articleData) {
      schemas.push({
        '@context': 'https://schema.org',
        '@type': 'Article',
        headline: title,
        description: description,
        author: {
          '@type': 'Person',
          name: articleData.author || '韩漫网',
        },
        datePublished: articleData.publishedTime,
        dateModified: articleData.modifiedTime || articleData.publishedTime,
        mainEntityOfPage: {
          '@type': 'WebPage',
          '@id': `${baseUrl}${canonical}`,
        },
      });
    }

    if (faqData && faqData.length > 0) {
      schemas.push({
        '@context': 'https://schema.org',
        '@type': 'FAQPage',
        mainEntity: faqData.map((faq) => ({
          '@type': 'Question',
          name: faq.question,
          acceptedAnswer: {
            '@type': 'Answer',
            text: faq.answer,
          },
        })),
      });
    }

    return schemas;
  };

  return (
    <>
      {buildSchema().map((schema, index) => (
        <script
          key={index}
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: JSON.stringify(schema) }}
        />
      ))}
    </>
  );
}